<template>
  <div class="personal-settings">
    <!-- 第一块：企业资料 -->
    <div class="personal-settings-section personal-info-section">
      <div class="personal-info-avatar-wrap">
        <!-- 头像容器 - auditStatus=1 时不显示修改按钮且禁用点击 -->
        <div 
          v-if="avatarDisplay.auditStatus !== 1" 
          class="personal-info-avatar" 
          @click="triggerAvatarUpload"
        >
          <img :src="avatarDisplay.src" alt="company avatar" />
          <span v-if="!avatarDisplay.src">LOGO</span>
          <div class="personal-info-avatar-mask">
            <span>修改</span>
          </div>
        </div>
        <!-- 审核中状态 - 不可点击 -->
        <div 
          v-else 
          class="personal-info-avatar personal-info-avatar--readonly"
        >
          <img :src="avatarDisplay.src" alt="company avatar" />
          <span v-if="!avatarDisplay.src">LOGO</span>
          <!-- 审核中提示（在头像内部） -->
          <div v-if="avatarDisplay.auditStatus === 1" class="personal-avatar-audit-tip">审核中</div>
        </div>
        <!-- 驳回提示 -->
        <div v-if="avatarDisplay.auditStatus === 3" class="personal-avatar-audit-tip personal-avatar-audit-tip--rejected">驳回</div>
        <input
          ref="avatarInput"
          type="file"
          accept="image/*"
          style="display: none"
          @change="onAvatarChange"
        />
      </div>
      <div class="personal-info-usercode">
        企业编号：{{ companyCode }}
      </div>
      <div class="personal-settings-row">
        <span class="personal-settings-label">企业简称</span>
        <span class="personal-settings-value">{{ company.shortName }}</span>
        <span class="personal-settings-link" @click="openShortNameDialog">修改</span>
      </div>
    </div>

    <!-- 第二块：登录信息 -->
    <div class="personal-settings-section">
      <div class="personal-settings-section-title">登录信息</div>
      <div class="personal-settings-row">
        <span class="personal-settings-label">绑定手机</span>
        <span class="personal-settings-value">{{ loginInfo.mobile }}</span>
        <span class="personal-settings-link" @click="openMobileDialog">修改</span>
      </div>
      <div class="personal-settings-row">
        <span class="personal-settings-label">登录密码</span>
        <span class="personal-settings-value">{{ loginInfo.password }}</span>
        <span class="personal-settings-link" @click="openPasswordDialog">修改</span>
      </div>
    </div>

    <!-- 第三块：企业认证信息 -->
    <div class="personal-settings-section">
      <div class="personal-settings-section-title">
        企业认证信息
        <span class="personal-auth-status-tag" :class="'personal-auth-status-tag--' + authStatus">{{ authStatusText }}</span>
      </div>
      <div v-if="authStatus === 3 && auditReason" class="personal-auth-reason">
        失败原因：{{ auditReason }}
      </div>
      <div class="company-auth-info-grid">
        <div class="company-auth-info-item">
          <span class="company-auth-info-label">行业</span>
          <span class="company-auth-info-value">{{ authInfo.industry }}</span>
        </div>
        <div class="company-auth-info-item">
          <span class="company-auth-info-label">企业名称</span>
          <span class="company-auth-info-value">{{ authInfo.companyName }}</span>
        </div>
        <div class="company-auth-info-item company-auth-info-item--full">
          <span class="company-auth-info-label">营业执照</span>
          <div class="company-auth-license-box">
            <img v-if="authInfo.license" :src="authInfo.license" alt="营业执照" />
            <span v-else>未上传营业执照</span>
          </div>
        </div>
        <div class="company-auth-info-item">
          <span class="company-auth-info-label">法人姓名</span>
          <span class="company-auth-info-value">{{ authInfo.legalName }}</span>
        </div>
        <div class="company-auth-info-item">
          <span class="company-auth-info-label">法人电话</span>
          <span class="company-auth-info-value">{{ authInfo.legalPhone }}</span>
        </div>
        <div class="company-auth-info-item company-auth-info-item--full">
          <span class="company-auth-info-label">法人身份证</span>
          <div class="company-auth-images">
            <div class="company-auth-image-box">
              <img v-if="authInfo.legalIdCardFront" :src="authInfo.legalIdCardFront" alt="法人身份证正面" />
              <span v-else>身份证正面</span>
            </div>
            <div class="company-auth-image-box">
              <img v-if="authInfo.legalIdCardBack" :src="authInfo.legalIdCardBack" alt="身份证国徽面" />
              <span v-else>身份证国徽面</span>
            </div>
          </div>
        </div>
        <div class="company-auth-info-item company-auth-info-item--full">
          <span class="company-auth-info-label">拥有品牌</span>
          <div class="company-auth-brands">
            <div v-for="(brand, index) in authInfo.brands" :key="index" class="company-auth-brand-item">
              <div class="company-auth-brand-image">
                <img v-if="brand.image" :src="brand.image" alt="品牌图片" />
                <span v-else>品牌图片</span>
              </div>
              <div class="company-auth-brand-name">{{ brand.name }}</div>
            </div>
            <div v-if="!authInfo.brands || authInfo.brands.length === 0" class="company-auth-brand-empty">暂无品牌</div>
          </div>
        </div>
      </div>
      <!-- 仅当认证状态为 0(未认证) 或 3(认证失败) 时才显示修改按钮 -->
      <div v-if="authStatus === 0 || authStatus === 3" class="personal-auth-edit-row">
        <span class="personal-auth-edit-btn" @click="openAuthDialog">修改</span>
      </div>
    </div>

    <!-- 修改企业简称弹窗 -->
    <van-dialog
      v-model="showShortNameDialog"
      title="修改企业简称"
      show-cancel-button
      @confirm="saveShortName"
      @cancel="showShortNameDialog = false"
    >
      <div class="personal-dialog-form">
        <van-field v-model="form.shortName" label="企业简称" placeholder="请输入企业简称" @input="handleShortNameInput" />
      </div>
    </van-dialog>

    <!-- 修改手机号弹窗 -->
    <van-dialog
      v-model="showMobileDialog"
      title="修改手机号码"
      width="390px"
      show-cancel-button
      :before-close="saveMobile"
      @cancel="showMobileDialog = false"
    >
      <div class="personal-mobile-dialog-form">
        <div class="mobile-dialog-row">
          <span class="mobile-dialog-label">登录手机号:</span>
          <span class="mobile-dialog-value" style="margin-left: 20px;">{{ loginInfo.mobile }}</span>
        </div>
        <div class="mobile-dialog-row">
          <span class="mobile-dialog-label">更换新手机号:</span>
          <van-field
            v-model="mobileForm.newMobile"
            placeholder="请输入新手机号"
            style="width: 68%;"
            maxlength="11"
          />
        </div>
        <div class="mobile-dialog-row">
          <span class="mobile-dialog-label">确认新手机号:</span>
          <van-field
            v-model="mobileForm.confirmNewMobile"
            placeholder="请再次输入新手机号"
            style="width: 68%;"
            maxlength="11"
          />
        </div>
        <!-- 图形验证码 -->
        <div class="mobile-dialog-row">
          <span class="mobile-dialog-label">图形验证码:</span>
          <div class="mobile-dialog-code-wrapper-flexible">
            <van-field
              v-model="mobileForm.captchaInput"
              placeholder="请输入图形验证码"
              maxlength="6"
              style="width: 68%;"
            />
            
            <!-- 图形验证码 - 右对齐 -->
            <div class="mobile-dialog-captcha-wrap-container">
              <div class="mobile-dialog-captcha-wrap-small" @click="refreshBindingCaptcha">
                <img v-if="bindingCaptchaUrl" :src="bindingCaptchaUrl" alt="图形验证码" style="object-fit: cover;" />
                <div v-else class="captcha-placeholder">点击获取</div>
              </div>
            </div>
          </div>
        </div>
                
        <!-- 原手机号验证码 -->
        <div class="mobile-dialog-row">
          <span class="mobile-dialog-label">原手机号验证码:</span>
          <div class="mobile-dialog-sms-wrapper-flexible">
            <van-field
              v-model="mobileForm.smsCode"
              placeholder="请输入短信验证码"
              maxlength="6"
              style="width: 68%;"
            />
            <button
              class="personal-dialog-code-btn"
              :disabled="countdown > 0"
              @click="handleSendMobileCode"
            >
              {{ countdown > 0 ? `${countdown}s` : '获取验证码' }}
            </button>
          </div>
        </div>
      </div>
    </van-dialog>

    <!-- 修改密码弹窗 -->
    <van-dialog
      v-model="showPasswordDialog"
      title="修改密码"
      width="380px"
      show-cancel-button
      :before-close="savePassword"
      @cancel="showPasswordDialog = false"
    >
      <div class="personal-dialog-form">
        <!-- 登录手机号 -->
        <div class="mobile-dialog-row">
          <span class="mobile-dialog-label">登录手机号:</span>
          <div class="mobile-dialog-value" style="margin-left: 24px;">{{ loginInfo.mobile }}</div>
        </div>
        
        <!-- 图形验证码 -->
        <div class="mobile-dialog-row">
          <span class="mobile-dialog-label">图形验证码:</span>
          <div class="mobile-dialog-code-wrapper-flexible">
            <van-field
              v-model="passwordForm.captchaInput"
              placeholder="请输入图形验证码"
              maxlength="6"
              style="width: 68%;margin-left: 11px;"
            />
            <!-- 图形验证码 - 右对齐 -->
            <div class="mobile-dialog-captcha-wrap-container">
              <div class="mobile-dialog-captcha-wrap-small" @click="refreshPasswordCaptcha">
                <img v-if="passwordCaptchaUrl" :src="passwordCaptchaUrl" alt="图形验证码" style="object-fit: cover;" />
                <div v-else class="captcha-placeholder">点击获取</div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 短信验证码 -->
        <div class="mobile-dialog-row">
          <span class="mobile-dialog-label">短信验证码:</span>
          <div class="mobile-dialog-sms-wrapper-flexible">
            <van-field
              v-model="passwordForm.smsCode"
              placeholder="请输入短信验证码"
              maxlength="6"
              style="width: 68%;margin-left: 11px;"
            />
            <button
              class="personal-dialog-code-btn"
              :disabled="passwordCountdown > 0"
              @click="handleSendPasswordCode" style="margin-left: -5px;"
            >
              {{ passwordCountdown > 0 ? `${passwordCountdown}s` : '获取验证码' }}
            </button>
          </div>
        </div>
        
        <van-field v-model="passwordForm.newPassword" label="新密码" placeholder="请输入新密码" type="password" />
        <div class="password-tip">密码长度必须大于8位，并且是复杂密码（包含大小写字母、数字、特殊符号中的至少 2 种）</div>
        <van-field v-model="passwordForm.confirmPassword" label="确认密码" placeholder="请再次输入新密码" type="password" />
      </div>
    </van-dialog>

    <!-- 修改企业认证信息弹窗 -->
    <van-dialog
      v-model="showAuthDialog"
      title="企业认证信息"
      class-name="company-auth-dialog"
      show-cancel-button
      :before-close="saveAuthInfo"
      @cancel="showAuthDialog = false"
    >
      <div class="personal-dialog-form personal-dialog-form--auth">
        <van-field v-model="form.industry" label="行业" maxlength="20" placeholder="请输入行业" />
        <van-field v-model="form.companyName" label="企业名称" maxlength="50" placeholder="请输入企业名称" />
        <div class="company-dialog-license-row">
          <span class="company-dialog-label">营业执照</span>
          <div class="company-dialog-license-box" @click="triggerLicenseUpload">
            <img v-if="form.license" :src="form.license" alt="营业执照" />
            <span v-else>上传营业执照</span>
          </div>
        </div>
        <input
          ref="licenseInput"
          type="file"
          accept="image/*"
          style="display: none"
          @change="onLicenseChange"
        />
        <div class="personal-dialog-field-grid">
          <van-field v-model="form.legalName" label="法人姓名" maxlength="10" placeholder="请输入法人姓名" />
          <van-field v-model="form.legalPhone" label="法人电话" maxlength="11" placeholder="请输入法人电话" />
        </div>
        <div class="company-dialog-idcard-row">
          <span class="company-dialog-label">法人身份证</span>
          <div class="company-dialog-idcard-images">
            <div class="company-dialog-idcard-box" @click="triggerIdCardUpload('front')">
              <img v-if="form.legalIdCardFront" :src="form.legalIdCardFront" alt="法人身份证正面" />
              <span v-else>上传身份证正面</span>
            </div>
            <div class="company-dialog-idcard-box" @click="triggerIdCardUpload('back')">
              <img v-if="form.legalIdCardBack" :src="form.legalIdCardBack" alt="法人身份证国徽面" />
              <span v-else>上传身份证国徽面</span>
            </div>
          </div>
        </div>
        <input
          ref="legalIdCardFrontInput"
          type="file"
          accept="image/*"
          style="display: none"
          @change="onIdCardChange($event, 'front')"
        />
        <input
          ref="legalIdCardBackInput"
          type="file"
          accept="image/*"
          style="display: none"
          @change="onIdCardChange($event, 'back')"
        />
        <input
          ref="brandInput"
          type="file"
          accept="image/*"
          style="display: none"
          @change="onBrandChange"
        />
        <div class="company-dialog-brands">
          <div class="company-dialog-brands-header">
            <span class="company-dialog-label">拥有品牌</span>
            <span class="company-dialog-add-brand" @click="addBrand">+ 新增品牌</span>
          </div>
          <div class="company-dialog-brand-list">
            <div v-for="(brand, index) in form.brands" :key="index" class="company-dialog-brand-item">
              <div class="company-dialog-brand-image" @click="triggerBrandUpload(index)">
                <img v-if="brand.image" :src="brand.image" alt="品牌图片" />
                <span v-else>上传品牌图片</span>
              </div>
              <van-field v-model="brand.name" placeholder="品牌名称" />
              <span class="company-dialog-brand-delete" @click="removeBrand(index)">删除</span>
            </div>
          </div>
        </div>
      </div>
    </van-dialog>
  </div>
</template>

<script>
import { Toast } from 'vant'
import { fetchCompanyInfo, updateCompanyAvatar, updateShortName, changeCompanyPhone, changeCompanyPassword, completeCompany, sendSmsCode, uploadFile } from '../../../api'
import { request } from '../../../api/request'
import CryptoJS from 'crypto-js'

export default {
  name: 'CompanyInfo',
  data() {
    return {
      company: {
        avatar: '',
        shortName: ''
      },
      // 新头像审核状态相关
      newAvatar: '',
      newAvatarAuditStatus: 0,
      companyCode: '',
      loginInfo: {
        mobile: '',
        password: '********'
      },
      authInfo: {
        industry: '',
        companyName: '',
        license: '',
        legalName: '',
        legalPhone: '',
        legalIdCardFront: '',
        legalIdCardBack: '',
        brands: []
      },
      authStatus: 0,
      auditReason: '',
      showShortNameDialog: false,
      showMobileDialog: false,
      showPasswordDialog: false,
      showAuthDialog: false,
      countdown: 0,
      timer: null,
      currentBrandIndex: -1,
      form: {
        shortName: '',
        industry: '',
        companyName: '',
        license: '',
        legalName: '',
        legalPhone: '',
        legalIdCardFront: '',
        legalIdCardBack: '',
        brands: []
      },
      mobileForm: {
        newMobile: '',
        confirmNewMobile: '',
        captchaInput: '',     // 图形验证码输入框
        smsCode: ''          // 短信验证码输入框
      },
      // 图形验证码相关
      bindingCaptchaUrl: '',
      passwordForm: {
        captchaInput: '',   // 图形验证码输入框
        smsCode: '',        // 短信验证码输入框
        newPassword: '',
        confirmPassword: ''
      },
      // 密码验证码相关
      passwordCountdown: 0,
      passwordTimer: null,
      passwordCaptchaUrl: '',
      passwordCaptchaUuid: '',
    }
  },
  created() {
    this.getCompanyInfo()
  },
  beforeDestroy() {
    this.clearTimer()
  },
  computed: {
    authStatusText() {
      const map = { 0: '未认证', 1: '待审核', 2: '已认证', 3: '认证失败' }
      return map[this.authStatus] || '未知'
    },
    /**
     * 头像显示逻辑
     * - newAvatarAuditStatus 为 1 时显示 newAvatar（审核中）
     * - newAvatarAuditStatus 为 3 时显示 newAvatar（驳回）
     * - 其他情况显示 avatar
     */
    avatarDisplay() {
      const auditStatus = this.newAvatarAuditStatus
      let src = ''
      
      if (auditStatus === 1 || auditStatus === 3) {
        // 审核中和驳回都显示 newAvatar
        src = this.newAvatar || ''
      } else {
        // 其他情况显示原头像
        src = this.company.avatar || ''
      }
      
      return {
        src,
        auditStatus
      }
    }
  },
  methods: {
    // 获取企业信息
    async getCompanyInfo() {
      try {
        const data = await fetchCompanyInfo()
        console.log('获取企业信息:', data)
        this.companyCode = data.companyCode || ''
        this.company = {
          avatar: data.avatar || '',
          shortName: data.companyShortName || ''
        }
        // 新头像审核状态相关字段
        this.newAvatar = data.newAvatar || ''
        this.newAvatarAuditStatus = data.newAvatarAuditStatus || 0
        this.loginInfo = {
          mobile: data.mobile || '',
          password: '********'
        }
        this.authStatus = data.auditStatus || 0
        this.auditReason = data.auditReason || ''
        this.authInfo = {
          industry: data.industry || '',
          companyName: data.companyName || '',
          license: data.businessLicenseImage || '',
          legalName: data.legalPersonName || '',
          legalPhone: data.legalPersonPhone || '',
          legalIdCardFront: data.legalPersonFrontImage || '',
          legalIdCardBack: data.legalPersonBackImage || '',
          brands: (data.companyBrandList || []).map(item => ({
            id: item.id,
            name: item.brandName || '',
            image: item.brandImage || ''
          }))
        }
        this.$emit('loaded')
      } catch (err) {
        Toast(err.message || '获取企业信息失败')
        this.$emit('load-error')
      }
    },
    // 头像上传
    triggerAvatarUpload() {
      this.$refs.avatarInput.click()
    },
    async onAvatarChange(e) {
      const file = e.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) {
        Toast('请选择图片文件')
        return
      }
      try {
        const url = await uploadFile(file)
        await updateCompanyAvatar(url)
        // 上传后重新获取用户信息，更新头像审核状态
        await this.getCompanyInfo()
        Toast('头像上传成功')
      } catch (err) {
        Toast(err.message || '头像上传失败')
      }
    },
    async uploadFile(file) {
      return uploadFile(file)
    },
    // 企业简称弹窗
    openShortNameDialog() {
      this.form.shortName = this.company.shortName
      this.showShortNameDialog = true
    },
    /** 企业简称输入长度限制（英文数字占 1 字符，汉字占 2 字符） */
    handleShortNameInput() {
      // 移除特殊字符
      const reg = /[^一 -\u9fa5a-zA-Z0-9]/g
      this.form.shortName = this.form.shortName.replace(reg, '')
      // 长度限制
      this.form.shortName = this.limitCharLength(this.form.shortName, 20)
    },
    /** 按字符权重截断字符串（英文数字占 1，汉字占 2） */
    limitCharLength(str, maxLen) {
      if (!str) return str
      let len = 0
      let i = 0
      for (; i < str.length; i++) {
        const charLen = str.charCodeAt(i) > 127 ? 2 : 1
        if (len + charLen > maxLen) break
        len += charLen
      }
      return str.substring(0, i)
    },
    /** 验证企业简称格式（只允许大小写字母、数字、汉字） */
    validateShortNameFormat(shortName) {
      const reg = /^[a-zA-Z0-9一-龥]+$/
      return reg.test(shortName)
    },
    async saveShortName() {
      const shortName = this.form.shortName.trim()
      if (!shortName) {
        Toast('企业简称不能为空')
        return
      }
      if (!this.validateShortNameFormat(shortName)) {
        Toast('企业简称只能包含大小写字母、数字和汉字')
        return
      }
      try {
        await updateShortName(shortName)
        this.company.shortName = shortName
        this.$emit('user-updated')
        Toast('企业简称修改成功')
      } catch (err) {
        Toast(err.message || '企业简称修改失败')
      }
    },
    // 修改手机号弹窗
    openMobileDialog() {
      this.mobileForm = {
        newMobile: '',
        confirmNewMobile: '',
        captchaInput: '',     // 图形验证码输入框
        smsCode: ''          // 短信验证码输入框
      }
      this.countdown = 0
      this.showMobileDialog = true
      this.loadBindingCaptcha()  // 打开弹窗时立即加载图形验证码
    },
    async saveMobile(action, done) {
      if (action !== 'confirm') { done(); return }
      const newMobile = this.mobileForm.newMobile.trim()
      const confirmNewMobile = this.mobileForm.confirmNewMobile.trim()
      const smsCode = this.mobileForm.smsCode.trim()
      
      if (!newMobile) {
        Toast('请输入新手机号')
        done(false);
        return
      }
      if (!/^1[3-9]\d{9}$/.test(newMobile)) {
        Toast('请输入正确的手机号')
        done(false);
        return
      }
      if (!confirmNewMobile) {
        Toast('请确认新手机号')
        done(false);
        return
      }
      if (newMobile !== confirmNewMobile) {
        Toast('两次输入的新手机号不一致')
        done(false);
        return
      }
      if (!smsCode) {
        Toast('请输入短信验证码')
        done(false);
        return
      }
      try {
        await changeCompanyPhone(smsCode, newMobile, '')
        this.loginInfo.mobile = newMobile
        Toast('手机号修改成功')
        this.showMobileDialog = false
      } catch (err) {
        Toast(err.message || '手机号修改失败')
        done(false);
      }
    },
    // 修改密码弹窗
    openPasswordDialog() {
      this.passwordForm = {
        captchaInput: '',   // 图形验证码输入框
        smsCode: '',        // 短信验证码输入框
        newPassword: '',
        confirmPassword: ''
      }
      this.passwordCountdown = 0
      this.showPasswordDialog = true
      this.loadPasswordCaptcha()  // 打开弹窗时立即加载图形验证码
    },
    async savePassword(action, done) {
      if (action !== 'confirm') { done(); return }  
      const smsCode = this.passwordForm.smsCode.trim()
      const newPassword = this.passwordForm.newPassword
      const confirmPassword = this.passwordForm.confirmPassword
      
      if (!smsCode) {
        Toast('请输入短信验证码')
        done(false);
        return
      }
      if (!newPassword) {
        
        Toast('请输入新密码')
        done(false);
        return
      }
      
      // 密码验证：长度 8-20 位，且至少包含两种字符类型 (大小写字母、数字、特殊符号)
      if (newPassword.length < 8 || newPassword.length > 20) {
        Toast('密码长度必须在 8-20 个字符之间')
        done(false);
        return
      }
      
      // 检查密码复杂度
      const hasUpper = /[A-Z]/.test(newPassword)
      const hasLower = /[a-z]/.test(newPassword)
      const hasDigit = /\d/.test(newPassword)
      const hasSpecial = /[!@#$%^&*()_+=][{};':"\\|,.<>?~`-]/.test(newPassword)
      
      const typeCount = [hasUpper, hasLower, hasDigit, hasSpecial].filter(Boolean).length
      
      if (typeCount < 2) {
       
        Toast('密码必须包含至少两种字符类型 (大小写字母、数字、特殊符号)')
         done(false);
        return
      }
      
      if (newPassword !== confirmPassword) {
        Toast('两次密码输入不一致')
        done(false);  
        return
      }
      
      try {
        // AES 加密密码（使用与登录页面相同的加密方法）
        const SECRET_KEY = CryptoJS.enc.Utf8.parse('zp_base_aes_2026')
        const wordArray = CryptoJS.enc.Utf8.parse(newPassword)
        const encrypted = CryptoJS.AES.encrypt(wordArray, SECRET_KEY, {
          mode: CryptoJS.mode.ECB,
          padding: CryptoJS.pad.Pkcs7
        }).toString()
        
        await changeCompanyPassword(this.loginInfo.mobile, smsCode, encrypted)
        this.loginInfo.password = '********'
        Toast('密码修改成功')
        this.showPasswordDialog = false
      } catch (err) {
        Toast(err.message || '密码修改失败')
         done(false);
      }
    },
    // 发送验证码 (修改手机号时使用)
    async handleSendMobileCode() {
      if (this.countdown > 0) return
      
      // 先检查图形验证码输入框
      if (!this.mobileForm.captchaInput) {
        Toast('请输入图形验证码')
        return
      }
      console.log('发送验证码', this.mobileForm.captchaInput, this.bindingCaptchaUuid)
      try {
        await sendSmsCode(null, {  // 不传 mobile 参数
          captchaCode: this.mobileForm.captchaInput,
          uuid: this.bindingCaptchaUuid
        },'company')
        this.startCountdown()
        Toast('验证码已发送')
        this.loadBindingCaptcha() // 刷新图片验证码
      } catch (err) {
        Toast(err.message || '验证码发送失败')
      }
    },
    
    // 加载图形验证码
    loadBindingCaptcha() {
      request('/api/captchaImage')
        .then(res => {
          if (res.code === 200) {
            this.bindingCaptchaUrl = `data:image/png;base64,${res.img}`
            this.bindingCaptchaUuid = res.uuid
          }
        })
        .catch(err => {
          console.error('获取验证码失败:', err)
        })
    },
    // 刷新图形验证码
    refreshBindingCaptcha() {
      this.loadBindingCaptcha()
    },
    // 刷新密码图形验证码
    refreshPasswordCaptcha() {
      this.loadPasswordCaptcha()
    },
    
    // 加载密码图形验证码
    loadPasswordCaptcha() {
      request('/api/captchaImage').then(res => {
        if (res.data?.img || res.img) {
          const url = res.data?.img || res.img || ''
          const uuid = res.data?.uuid || res.uuid || ''
          if (url) {
            this.passwordCaptchaUrl = `data:image/png;base64,${url}`
            this.passwordCaptchaUuid = uuid
          }
        }
      }).catch(err => {
        console.error('获取密码验证码失败:', err)
      })
    },
    
    // 发送密码验证码
    async handleSendPasswordCode() {
      if (this.passwordCountdown > 0) return
      
      // 先检查图形验证码输入框
      if (!this.passwordForm.captchaInput) {
        Toast('请输入图形验证码')
        return
      }
      
      try {
        await sendSmsCode(null, {  // 不传 mobile 参数
          captchaCode: this.passwordForm.captchaInput,
          uuid: this.passwordCaptchaUuid
        },'company')
        this.startPasswordCountdown()
        Toast('验证码已发送')
        this.loadPasswordCaptcha() // 刷新图片验证码
      } catch (err) {
        Toast(err.message || '验证码发送失败')
      }
    },
    startCountdown() {
      this.countdown = 60
      this.timer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(this.timer)
          this.timer = null
        }
      }, 1000)
    },
    
    // 密码验证码倒计时
    startPasswordCountdown() {
      this.passwordCountdown = 60
      this.passwordTimer = setInterval(() => {
        this.passwordCountdown--
        if (this.passwordCountdown <= 0) {
          clearInterval(this.passwordTimer)
          this.passwordTimer = null
        }
      }, 1000)
    },
    clearTimer() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
      if (this.passwordTimer) {
        clearInterval(this.passwordTimer)
        this.passwordTimer = null
      }
    },
    // 企业认证弹窗
    openAuthDialog() {
      this.form = {
        ...this.form,
        ...this.authInfo,
        brands: (this.authInfo.brands || []).map(b => ({ ...b }))
      }
      this.showAuthDialog = true
    },
    async saveAuthInfo(action, done) {
       if (action !== 'confirm') { done(); return } 
      const requiredFields = ['industry', 'companyName', 'legalName', 'legalPhone']
      for (const field of requiredFields) {
        if (!this.form[field] || !String(this.form[field]).trim()) {
          Toast('请完善企业认证信息')
          done(false);
          return
        }
      }
      if (!this.form.license) {
        Toast('请上传营业执照')
        done(false);  
        return
      }
      if (!this.form.legalIdCardFront || !this.form.legalIdCardBack) {
        Toast('请上传法人身份证正反面')
        done(false);
        return
      }
      const params = {
        industry: this.form.industry,
        companyName: this.form.companyName,
        businessLicenseImage: this.form.license,
        legalPersonName: this.form.legalName,
        legalPersonPhone: this.form.legalPhone,
        legalPersonFrontImage: this.form.legalIdCardFront,
        legalPersonBackImage: this.form.legalIdCardBack,
        companyBrandList: (this.form.brands || []).map(item => {
          const brandItem = {
            brandName: item.name,
            brandImage: item.image
          }
          if (item.id) {
            brandItem.id = item.id
          }
          return brandItem
        })
      }
      try {
        await completeCompany(params)
        this.authInfo = {
          industry: this.form.industry,
          companyName: this.form.companyName,
          license: this.form.license,
          legalName: this.form.legalName,
          legalPhone: this.form.legalPhone,
          legalIdCardFront: this.form.legalIdCardFront,
          legalIdCardBack: this.form.legalIdCardBack,
          brands: (this.form.brands || []).map(b => ({ ...b }))
        }
        this.authStatus = 1
        Toast('企业认证信息提交成功')
        this.showAuthDialog = false
      } catch (err) {
        Toast(err.message || '企业认证信息提交失败')
        done(false);
      }
    },
    // 营业执照上传
    triggerLicenseUpload() {
      this.$refs.licenseInput.click()
    },
    async onLicenseChange(e) {
      const file = e.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) {
        Toast('请选择图片文件')
        return
      }
      try {
        const url = await this.uploadFile(file)
        this.form.license = url
      } catch (err) {
        Toast(err.message || '图片上传失败')
      }
    },
    // 法人身份证上传
    triggerIdCardUpload(type) {
      const ref = type === 'front' ? 'legalIdCardFrontInput' : 'legalIdCardBackInput'
      this.$refs[ref].click()
    },
    async onIdCardChange(e, type) {
      const file = e.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) {
        Toast('请选择图片文件')
        return
      }
      try {
        const url = await this.uploadFile(file)
        const key = type === 'front' ? 'legalIdCardFront' : 'legalIdCardBack'
        this.form[key] = url
      } catch (err) {
        Toast(err.message || '图片上传失败')
      }
    },
    // 品牌管理
    addBrand() {
      this.form.brands.push({
        image: '',
        name: ''
      })
    },
    removeBrand(index) {
      this.form.brands.splice(index, 1)
    },
    triggerBrandUpload(index) {
      this.currentBrandIndex = index
      this.$refs.brandInput.click()
    },
    async onBrandChange(e) {
      const file = e.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) {
        Toast('请选择图片文件')
        return
      }
      try {
        const url = await this.uploadFile(file)
        if (this.currentBrandIndex >= 0 && this.form.brands[this.currentBrandIndex]) {
          this.form.brands[this.currentBrandIndex].image = url
        }
      } catch (err) {
        Toast(err.message || '图片上传失败')
      }
    }
  }
}
</script>

<style scoped>
.personal-info-section {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.personal-info-avatar-wrap {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.personal-info-avatar {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: #F5FAFF;
  border: 1px solid #E4F2FF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #B2B2B2;
  overflow: hidden;
  cursor: pointer;
}

/* 只读模式（审核中）- 无点击效果 */
.personal-info-avatar--readonly {
  cursor: default;
}

.personal-info-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.personal-info-avatar-mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.personal-info-avatar:hover .personal-info-avatar-mask {
  opacity: 1;
}

.personal-info-avatar-mask span {
  color: #FFFFFF;
  font-size: 14px;
}

.personal-info-usercode {
  font-size: 13px;
  color: #999999;
  margin-bottom: 12px;
  text-align: center;
}

.company-auth-info-grid {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.company-auth-info-item {
  display: flex;
  align-items: flex-start;
  font-size: 13px;
  min-width: 0;
}

.company-auth-info-item--full {
  flex-direction: column;
}

.company-auth-info-label {
  color: #666666;
  width: 80px;
  flex-shrink: 0;
  line-height: 20px;
}

.company-auth-info-value {
  color: #333333;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 20px;
}

.company-auth-license-box {
  width: 220px;
  height: 140px;
  background: #F5FAFF;
  border: 1px solid #E4F2FF;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #B2B2B2;
  font-size: 13px;
  overflow: hidden;
  margin-top: 8px;
}

.company-auth-license-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.company-auth-images {
  display: flex;
  gap: 20px;
  margin-top: 8px;
}

.company-auth-image-box {
  width: 220px;
  height: 140px;
  background: #F5FAFF;
  border: 1px solid #E4F2FF;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #B2B2B2;
  font-size: 13px;
  overflow: hidden;
}

.company-auth-image-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.company-auth-brands {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
}

.company-auth-brand-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.company-auth-brand-image {
  width: 100px;
  height: 100px;
  background: #F5FAFF;
  border: 1px solid #E4F2FF;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #B2B2B2;
  font-size: 12px;
  overflow: hidden;
}

.company-auth-brand-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.company-auth-brand-name {
  font-size: 13px;
  color: #333333;
}

.company-auth-brand-empty {
  font-size: 13px;
  color: #999999;
}

.personal-auth-edit-row {
  display: flex;
  justify-content: center;
  padding-top: 16px;
  border-top: 1px solid #E4F2FF;
  margin-top: 16px;
}

.personal-auth-edit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 22px;
  background: #0084E7;
  color: #FFFFFF;
  font-size: 12px;
  border-radius: 14px;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.personal-auth-edit-btn:hover {
  opacity: 0.85;
}

.personal-auth-status-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  padding: 0 10px;
  margin-left: 10px;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 400;
  vertical-align: middle;
}

.personal-auth-status-tag--0 {
  background: #F5F5F5;
  color: #999999;
}

.personal-auth-status-tag--1 {
  background: #FFF7E6;
  color: #FAAD14;
}

.personal-auth-status-tag--2 {
  background: #F6FFED;
  color: #52C41A;
}

.personal-auth-status-tag--3 {
  background: #FFF1F0;
  color: #FF4D4F;
}

.personal-auth-reason {
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #FFF1F0;
  border: 1px solid #FFCCC7;
  border-radius: 4px;
  color: #FF4D4F;
  font-size: 13px;
  line-height: 1.5;
}

.personal-dialog-form {
  padding: 16px 20px;
}

.password-tip {
  font-size: 12px;
  color: #999999;
  line-height: 1.4;
}
</style>

<style>
/* 头像审核状态提示 */
.personal-avatar-audit-tip {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  padding: 2px 10px;
  border-radius: 3px;
  font-size: 14px;
  line-height: 1.4;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
}

/* 审核中 - 蓝色 */
.personal-avatar-audit-tip:not(.personal-avatar-audit-tip--rejected) {
  background: #E1F3F8;
  color: #23B4DC;
}

/* 驳回 - 红色 */
.personal-avatar-audit-tip--rejected {
  background: #FFF1F0;
  color: #FF4D4F;
}

/* 修改手机号弹窗样式 */
.personal-mobile-dialog-form {
  padding: 20px 28px;
}

.mobile-dialog-row {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.mobile-dialog-label {
  width: 110px;
  font-size: 14px;
  color: #323233;
  text-align: right;
  flex-shrink: 0;
}

.mobile-dialog-value {
  width: calc(100% - 110px);
  font-size: 14px;
  color: #323233;
}

/* 图形验证码、短信验证码和按钮行 */
.mobile-dialog-code-input-wrapper {
  flex: 1;
  display: flex;
  gap: 8px;
  align-items: center;
}

/* 图形验证码 */
.mobile-dialog-captcha-wrap {
  flex-shrink: 0;
  width: 90px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  background: #F5F5F5;
}

.mobile-dialog-captcha-wrap img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.captcha-placeholder {
  font-size: 12px;
  color: #666666;
  text-align: center;
  line-height: 40px;
}

/* 获取验证码按钮 */
.mobile-dialog-get-code-btn {
  flex-shrink: 0;
  height: 40px;
  padding: 0 16px;
  border: none;
  border-radius: 4px;
  background: #FFFFFF;
  color: #323233;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}

.mobile-dialog-get-code-btn:hover:not(:disabled) {
  background: #F5F5F5;
}

.mobile-dialog-get-code-btn:disabled {
  color: #C8C9CC;
  cursor: not-allowed;
}

/* 企业认证弹窗加宽 */
.company-auth-dialog {
  width: 600px;
  max-width: 90vw;
}

.company-auth-dialog .van-dialog__content {
  max-height: 70vh;
  overflow-y: auto;
}

.company-auth-dialog .personal-dialog-form--auth {
  padding: 20px 28px;
}

.company-auth-dialog .personal-dialog-field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
}

.company-auth-dialog .personal-dialog-field-grid .van-field {
  margin: 0;
}

.company-auth-dialog .personal-dialog-field-grid .van-field__label {
  width: 80px;
}

/* 营业执照上传 */
.company-dialog-license-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}

.company-dialog-label {
  font-size: 14px;
  color: #646566;
  margin-bottom: 8px;
}

.company-dialog-license-box {
  width: 220px;
  height: 140px;
  background: #F5FAFF;
  border: 1px solid #E4F2FF;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #B2B2B2;
  font-size: 13px;
  cursor: pointer;
  overflow: hidden;
}

.company-dialog-license-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 法人身份证上传 */
.company-dialog-idcard-row {
  display: flex;
  flex-direction: column;
  margin: 12px 0;
}

.company-dialog-idcard-images {
  display: flex;
  gap: 16px;
}

.company-dialog-idcard-box {
  flex: 1;
  height: 120px;
  background: #F5FAFF;
  border: 1px solid #E4F2FF;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #B2B2B2;
  font-size: 13px;
  cursor: pointer;
  overflow: hidden;
}

.company-dialog-idcard-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 品牌管理 */
.company-dialog-brands {
  margin-top: 12px;
}

.company-dialog-brands-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.company-dialog-add-brand {
  color: #0084E7;
  font-size: 13px;
  cursor: pointer;
}

.company-dialog-brand-list {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.company-dialog-brand-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 120px;
}

.company-dialog-brand-image {
  width: 100px;
  height: 100px;
  background: #F5FAFF;
  border: 1px solid #E4F2FF;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #B2B2B2;
  font-size: 12px;
  cursor: pointer;
  overflow: hidden;
}

.company-dialog-brand-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.company-dialog-brand-item .van-field {
  margin: 0;
  padding: 4px 8px;
  background: #F5FAFF;
  border-radius: 4px;
}

.company-dialog-brand-delete {
  color: #FF4D4F;
  font-size: 12px;
  cursor: pointer;
}

/* 弹窗验证码输入行 */
.personal-dialog-code-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.personal-dialog-code-row .van-field {
  flex: 1;
  margin: 0;
}

.personal-dialog-code-btn {
  flex-shrink: 0;
  height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 17px;
  background-color: rgba(35, 180, 220, 0.1);
  color: #23B4DC;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
  white-space: nowrap;
}

.personal-dialog-code-btn:hover:not(:disabled) {
  background-color: rgba(35, 180, 220, 0.18);
}

.personal-dialog-code-btn:disabled {
  color: rgba(150, 150, 150, 1);
  background-color: rgba(200, 200, 200, 0.15);
  cursor: not-allowed;
}
</style>
